import { useCallback, useEffect } from 'react';

import { useMultipleSelect } from 'components/modules/Insurance/hooks/useMultipleSelect';
import { getChangeEvent } from 'components/modules/Insurance/Insurance.helpers';
import { FIELDS } from 'store/insuranceAnketa/constants';

import { useMaxDate } from '../../hooks/useMaxDate';
import { useDisabledFields } from '../../hooks/useDisabledFields';
import { useStoreHelper } from '../../hooks/useStoreHelper';

import { useManualAddress } from './hooks/useManualAddress';

import {
  ADDRESS_TYPES,
  DEFAULT_VALUES,
  NAMES,
  STORE_FALLBACK,
  STORE_MAP,
} from './Passport.constants';
import { addressHandler, issuerInfoHandler } from './Passport.helpers';

export const usePassportPresenter = ({ isOwnerForm }) => {
  const { errorsSelector, onChangeValue, valuesSelector } = useStoreHelper({ isOwnerForm });
  const { disabled: pageRestrict } = useDisabledFields();

  const { values: errors } = useMultipleSelect({ map: STORE_MAP, selector: errorsSelector });
  const { values } = useMultipleSelect({
    fallback: STORE_FALLBACK,
    map: STORE_MAP,
    selector: valuesSelector,
  });
  const {
    [NAMES.LIVING_ADDRESS_EQUALS]: livingAddressEquals,
    [NAMES.PASSPORT_DATE_ISSUE]: passportDateIssue,
  } = values;

  const { maxDate: maxPreviousPassportDate } = useMaxDate({ date: passportDateIssue });

  const onChange = useCallback(
    (event) => {
      const { key, value } = getChangeEvent(event);
      const params = [];

      switch (key) {
        case NAMES.LIVING_ADDRESS:
        case NAMES.MANUAL_LIVING_ADDRESS:
          params.push(...addressHandler({ type: ADDRESS_TYPES.LIVING, value }));
          break;

        case NAMES.MANUAL_REGISTRATION_ADDRESS:
        case NAMES.REGISTRATION_ADDRESS:
          params.push(...addressHandler({ type: ADDRESS_TYPES.REGISTRATION, value }));
          break;

        case NAMES.PASSPORT_ISSUER:
        case NAMES.PASSPORT_ISSUER_CODE:
          params.push(...issuerInfoHandler({ value }));
          break;

        default:
          params.push({ key: STORE_MAP[key], value });
      }

      for (const param of params) {
        onChangeValue(param);
      }
    },
    [onChangeValue]
  );

  const manualLivingAddress = useManualAddress({
    name: NAMES.MANUAL_LIVING_ADDRESS,
    onChange,
  });
  const manualRegistrationAddress = useManualAddress({
    name: NAMES.MANUAL_REGISTRATION_ADDRESS,
    onChange,
  });

  useEffect(
    () => {
      // TODO: Возможно не только на булевый признак нужно обращать внимание
      if (typeof livingAddressEquals === 'boolean') {
        return;
      }

      onChangeValue({ key: FIELDS.LIVING_ADDRESS_EQUALS, value: true });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [livingAddressEquals]
  );

  return {
    disabled: pageRestrict,
    errors,
    manualLivingAddress,
    manualRegistrationAddress,
    maxPreviousPassportDate,
    onChange,
    values: { ...DEFAULT_VALUES, ...values },
  };
};
